import type {
  CreateAbsoluteQuantityAdjustmentDTO,
  CreateCompanyHolidaysDTO,
  CreatePercentageQuantityAdjustmentDTO,
  CreateReferenceBaseloadCorrectionAdjustmentDTO,
  CreateRenewableProductionAdjustmentDTO,
  Energy,
  Power,
} from '@/src/api/types/procurement'
import type {
  PreviewAbsoluteQuantityAdjustmentDTO,
  PreviewCompanyHolidaysDTO,
  PreviewPercentageQuantityAdjustmentDTO,
  PreviewReferenceBaseloadCorrectionAdjustmentDTO,
  PreviewRenewableProductionAdjustmentDTO,
} from '@/src/api/types/procurement'
import { ALL_LOCATIONS } from '@/src/contexts/LocationsContext'
import type { ProcurementEnergyType } from '@/src/features/procurement-and-markets/load-planning/LoadPlanning.types'
import { snapToNextDay } from '@/src/utils/dateUtil'
import {
  type AnnualProductionUnit,
  LoadAdjustmentFormTypeEnum,
  type LoadPlanningAdjustmentFormValidated,
  type QuantityAdjustmentType,
  type RenewableSourceProfile,
  uppercaseWeekdays,
} from './LoadPlanningAdjustment.types'
import type { AdjustmentRow } from './table/adjustmentTable.types'

export type MapperContext = {
  companyId: number
  energyType: ProcurementEnergyType
  year: string
}

/**
 * Normalizes the form's locationId into a scope + locationId pair.
 * The form uses the ALL_LOCATIONS sentinel to represent a company-wide adjustment,
 * which maps to scope COMPANY with no locationId.
 */
const mapScope = (locationId: number | undefined): { scope: 'LOCATION' | 'COMPANY'; locationId?: number } => {
  if (locationId === undefined || locationId === ALL_LOCATIONS) {
    return { scope: 'COMPANY', locationId: undefined }
  }
  return { scope: 'LOCATION', locationId }
}

type PreviewRequestBody =
  | PreviewAbsoluteQuantityAdjustmentDTO
  | PreviewCompanyHolidaysDTO
  | PreviewPercentageQuantityAdjustmentDTO
  | PreviewReferenceBaseloadCorrectionAdjustmentDTO
  | PreviewRenewableProductionAdjustmentDTO

const KWH_PER_UNIT: Record<AnnualProductionUnit, number> = { kWh: 1, MWh: 1_000, GWh: 1_000_000 }

/**
 * The backend expects the annual production in kWh, while the form lets the user pick the unit
 */
const toAnnualProductionKwh = (data: LoadPlanningAdjustmentFormValidated): number => {
  return Number(data.totalAnnualProduction!) * KWH_PER_UNIT[data.totalAnnualProductionUnit as AnnualProductionUnit]
}

/**
 * Maps the annual production returned by the backend to MWh, the form's default unit
 */
export const toAnnualProductionMwh = (energy: Energy): number => {
  const unit = energy.unit === 'KWh' ? 'kWh' : energy.unit
  return (energy.amount * KWH_PER_UNIT[unit]) / KWH_PER_UNIT.MWh
}

/**
 * Maps form data to preview DTO for company holidays adjustment
 */
const mapToPreviewCompanyHolidaysDTO = (data: LoadPlanningAdjustmentFormValidated): PreviewCompanyHolidaysDTO => {
  return {
    id: data.id,
    adjustmentType: 'COMPANY_HOLIDAYS',
    name: data.name,
    ...mapScope(data.locationId),
    comment: data.comment,
    timeframeStart: data.timeframeStart,
    timeframeEnd: data.timeframeEnd,
    baseLoad: data.baseLoad!,
  }
}

/**
 * Maps form data to preview DTO for percentage quantity adjustment
 */
const mapToPreviewPercentageQuantityAdjustmentDTO = (data: LoadPlanningAdjustmentFormValidated): PreviewPercentageQuantityAdjustmentDTO => {
  return {
    id: data.id,
    adjustmentType: 'PERCENTAGE_QUANTITY',
    name: data.name,
    ...mapScope(data.locationId),
    comment: data.comment,
    timeframeStart: data.timeframeStart,
    timeframeEnd: data.timeframeEnd,
    applicableWeekdays: uppercaseWeekdays(data.applicableWeekdays),
    timeFrom: data.timeFrom,
    timeTo: data.timeTo,
    adjustmentValue: data.adjustmentValue!,
  }
}

/**
 * Maps form data to preview DTO for absolute quantity adjustment
 */
const mapToPreviewAbsoluteQuantityAdjustmentDTO = (data: LoadPlanningAdjustmentFormValidated): PreviewAbsoluteQuantityAdjustmentDTO => {
  const unitMap = { kW: 'KW', kWh: 'KWh' } as const
  const unit = unitMap[data.adjustmentValueUnit as 'kW' | 'kWh']

  return {
    id: data.id,
    adjustmentType: 'ABSOLUTE_QUANTITY',
    name: data.name,
    ...mapScope(data.locationId),
    comment: data.comment,
    timeframeStart: data.timeframeStart,
    timeframeEnd: data.timeframeEnd,
    applicableWeekdays: uppercaseWeekdays(data.applicableWeekdays),
    timeFrom: data.timeFrom,
    timeTo: data.timeTo,
    adjustmentValue: { unit, amount: Number(data.adjustmentValue!) } as Energy | Power,
  }
}

/**
 * Maps form data to preview DTO for reference period adjustment
 */
const mapToPreviewReferenceBaseloadCorrectionDTO = (
  data: LoadPlanningAdjustmentFormValidated
): PreviewReferenceBaseloadCorrectionAdjustmentDTO => {
  return {
    id: data.id,
    adjustmentType: 'REFERENCE_BASELOAD_CORRECTION',
    name: data.name,
    ...mapScope(data.locationId),
    comment: data.comment,
    timeframeStart: data.timeframeStart,
    timeframeEnd: data.timeframeEnd,
    referencePeriodStart: data.referencePeriodStart!,
    referencePeriodEnd: snapToNextDay(data.referencePeriodEnd!).toISOString(),
    percentageAdjustmentValue: data.percentageAdjustmentValue ?? 0,
  }
}

/**
 * Maps form data to preview DTO for renewable production adjustment
 */
const mapToPreviewRenewableProductionDTO = (data: LoadPlanningAdjustmentFormValidated): PreviewRenewableProductionAdjustmentDTO => {
  return {
    id: data.id,
    adjustmentType: 'RENEWABLE_PRODUCTION',
    name: data.name,
    ...mapScope(data.locationId),
    comment: data.comment,
    timeframeStart: data.timeframeStart,
    timeframeEnd: data.timeframeEnd,
    totalAnnualProduction: toAnnualProductionKwh(data),
    sourceProfile: data.sourceProfile as RenewableSourceProfile,
  }
}

/**
 * Maps validated form data to the appropriate preview DTO based on adjustment type
 * Used for preview/calculation requests before creating the actual adjustment
 */
export const mapFormDataToPreviewDTO = (data: LoadPlanningAdjustmentFormValidated): PreviewRequestBody => {
  if (data.type === LoadAdjustmentFormTypeEnum.COMPANY_HOLIDAYS) {
    return mapToPreviewCompanyHolidaysDTO(data)
  }

  if (data.type === LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT) {
    if (data.adjustmentType === 'percentage') {
      return mapToPreviewPercentageQuantityAdjustmentDTO(data)
    }
    if (data.adjustmentType === 'absolute') {
      return mapToPreviewAbsoluteQuantityAdjustmentDTO(data)
    }
  }

  if (data.type === LoadAdjustmentFormTypeEnum.REFERENCE_PERIOD) {
    return mapToPreviewReferenceBaseloadCorrectionDTO(data)
  }

  if (data.type === LoadAdjustmentFormTypeEnum.RENEWABLE_PRODUCTION) {
    return mapToPreviewRenewableProductionDTO(data)
  }

  throw new Error(`No mapper implementation for adjustment type ${data.type}`)
}

/**
 * Maps form data to creation DTO for company holidays adjustment
 */
export const mapToCreateCompanyHolidaysDTO = (
  data: LoadPlanningAdjustmentFormValidated,
  context: MapperContext
): CreateCompanyHolidaysDTO => {
  return {
    name: data.name,
    ...mapScope(data.locationId),
    companyId: context.companyId,
    energyType: context.energyType,
    comment: data.comment,
    year: context.year,
    timeframeStart: data.timeframeStart,
    timeframeEnd: data.timeframeEnd,
    baseLoad: data.baseLoad!,
  }
}

/**
 * Maps form data to creation DTO for percentage quantity adjustment
 */
export const mapToCreatePercentageQuantityAdjustmentDTO = (
  data: LoadPlanningAdjustmentFormValidated,
  context: MapperContext
): CreatePercentageQuantityAdjustmentDTO => {
  return {
    name: data.name,
    ...mapScope(data.locationId),
    companyId: context.companyId,
    energyType: context.energyType,
    comment: data.comment,
    year: context.year,
    timeframeStart: data.timeframeStart,
    timeframeEnd: data.timeframeEnd,
    applicableWeekdays: uppercaseWeekdays(data.applicableWeekdays),
    timeFrom: data.timeFrom,
    timeTo: data.timeTo,
    adjustmentValue: data.adjustmentValue!,
  }
}

/**
 * Maps form data to creation DTO for absolute quantity adjustment
 */
export const mapToCreateAbsoluteQuantityAdjustmentDTO = (
  data: LoadPlanningAdjustmentFormValidated,
  context: MapperContext
): CreateAbsoluteQuantityAdjustmentDTO => {
  const unitMap = { kW: 'KW', kWh: 'KWh' } as const
  const unit = unitMap[data.adjustmentValueUnit as 'kW' | 'kWh']

  return {
    name: data.name,
    ...mapScope(data.locationId),
    companyId: context.companyId,
    energyType: context.energyType,
    comment: data.comment,
    year: context.year,
    timeframeStart: data.timeframeStart,
    timeframeEnd: data.timeframeEnd,
    applicableWeekdays: uppercaseWeekdays(data.applicableWeekdays),
    timeFrom: data.timeFrom,
    timeTo: data.timeTo,
    adjustmentValue: { unit, amount: Number(data.adjustmentValue!) } as Energy | Power,
  }
}

/**
 * Maps form data to creation DTO for reference period adjustment
 */
export const mapToCreateReferenceBaseloadCorrectionDTO = (
  data: LoadPlanningAdjustmentFormValidated,
  context: MapperContext
): CreateReferenceBaseloadCorrectionAdjustmentDTO => {
  return {
    name: data.name,
    ...mapScope(data.locationId),
    companyId: context.companyId,
    energyType: context.energyType,
    comment: data.comment,
    year: context.year,
    timeframeStart: data.timeframeStart,
    timeframeEnd: data.timeframeEnd,
    referencePeriodStart: data.referencePeriodStart!,
    referencePeriodEnd: snapToNextDay(data.referencePeriodEnd!).toISOString(),
    percentageAdjustmentValue: data.percentageAdjustmentValue ?? 0,
  }
}

/**
 * Maps form data to creation DTO for renewable production adjustment
 */
export const mapToCreateRenewableProductionDTO = (
  data: LoadPlanningAdjustmentFormValidated,
  context: MapperContext
): CreateRenewableProductionAdjustmentDTO => {
  return {
    name: data.name,
    ...mapScope(data.locationId),
    companyId: context.companyId,
    energyType: context.energyType,
    comment: data.comment,
    year: context.year,
    timeframeStart: data.timeframeStart,
    timeframeEnd: data.timeframeEnd,
    totalAnnualProduction: toAnnualProductionKwh(data),
    sourceProfile: data.sourceProfile as RenewableSourceProfile,
  }
}

export const mapAdjustmentStringToEnum = (adjustmentType: AdjustmentRow['type']) => {
  switch (adjustmentType) {
    case 'companyHolidays':
      return LoadAdjustmentFormTypeEnum.COMPANY_HOLIDAYS
    case 'absoluteQuantity':
      return LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT
    case 'percentageQuantity':
      return LoadAdjustmentFormTypeEnum.QUANTITY_ADJUSTMENT
    case 'referenceBaseloadCorrection':
      return LoadAdjustmentFormTypeEnum.REFERENCE_PERIOD
    case 'renewableProduction':
      return LoadAdjustmentFormTypeEnum.RENEWABLE_PRODUCTION
  }
}

export const mapQuantityAdjustmentType = (adjustmentType: AdjustmentRow['type']): QuantityAdjustmentType | undefined => {
  switch (adjustmentType) {
    case 'absoluteQuantity':
      return 'absolute'
    case 'percentageQuantity':
      return 'percentage'
  }
}
